import {getCollectionTypeColor} from '@utils/const/collection';
import {Fragment, useEffect, useId, useMemo, useRef, useState} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faHeart, faSpinner, faXmark} from '@fortawesome/free-solid-svg-icons';
import {faHeart as faHeartOutline} from '@fortawesome/free-regular-svg-icons';
import {ThemeAwareSelect} from '@components/theme/ThemeAwareSelect';
import {useTheme} from '@components/theme/useTheme';
import {transformData} from '@components/ux/ProblemCollection/functions/transformData';
import {colorStyles} from '@components/ux/ProblemCollection/functions/stylesWithColors';
import useColorManager from '@components/ux/ProblemCollection/functions/useColorManager';
import {
    OptionType,
    OptionTypeWithLabel,
    Props,
} from '@components/ux/ProblemCollection/functions/types';
import {
    Modal,
    OverlayTrigger,
    Tooltip,
    Popover,
    Button,
    Form,
} from '@goproblems/ui';
import {DiamondFill, InfoCircle} from '@goproblems/ui/icons';
import {Link} from 'react-router';
import {MenuListProps} from 'react-select';
import CollectionLegend from './CollectionLegend';
import TrainingProblemToggle from '@components/ux/ProblemCollection/TrainingProblemToggle';
import {ProblemDestinationToggle} from './ProblemDestinationToggle';
import {ConfirmModal} from '@components/modals/ConfirmModal';
import {
    useAddToFavoritesMutation,
    useCreateCollectionSuggestionsMutation,
    useFetchCollectionPreviewOwnedAndFriendsQuery,
    useFetchCollectionPreviewRespectCollaborationQuery,
    useFetchCollectionSuggestionsQuery,
    useFetchLastProblemCollectionIdsQuery,
    useGetCollectionRespectPrivacyQuery,
    useRemoveFromFavoritesMutation,
    useSaveLastProblemCollectionIdMutation,
    useSaveProblemCollectionsMutation,
    problemsApi,
} from '@api';
import {
    CollectionSuggestionTypeEnum,
    SuggestedProblemEditStatusEnum,
} from '@types';
import {toast} from 'react-toastify';
import {useSelector, useDispatch} from '@utils';
import {UserItem} from '@components';
import {useTranslation} from 'react-i18next';

const EMPTY_COLLECTIONS: NonNullable<Props['collections']> = [];
const DEFAULT_COLLECTION_COLOR = '#737373';

const uniqueOptionsByValue = (items: OptionType[]): OptionType[] =>
    items.filter(
        (item, index, allItems) =>
            index === allItems.findIndex(other => other.value === item.value)
    );

const ProblemCollectionDropdown = ({
    isFavourite,
    collections = EMPTY_COLLECTIONS,
    problemId,
    userId,
    isEditable,
    openRef,
    beforeAddTo,
}: Props) => {
    const {t} = useTranslation();
    const allCategory = t('common.all');
    const myCollectionsCategory = t('problem_collections.add_to.my_collections');
    const lastUsedCategory = t('problem_collections.add_to.last_used');
    const searchCategory = t('common.search');
    const modalTitleId = useId();
    const [options, setOptions] = useState<OptionTypeWithLabel[]>([]);
    const [favourite, setFavourite] = useState<boolean>(isFavourite);
    const [selected, setSelected] = useState<OptionType[]>([]);
    const [savedSelection, setSavedSelection] = useState<OptionType[]>([]);
    const [isSavingCollections, setIsSavingCollections] = useState(false);
    const [collectionSaveError, setCollectionSaveError] = useState<string | null>(
        null
    );
    const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
    const collectionSaveInFlight = useRef(false);
    const lastUsedCollectionUpdates = useRef<Promise<void>>(Promise.resolve());
    const draftDirty = useRef(false);
    const appliedCollectionSource = useRef<{
        problemId: number;
        key: string;
    } | null>(null);
    const [isUpdatingFavorite, setIsUpdatingFavorite] = useState(false);
    const favoriteUpdateInFlight = useRef(false);
    const {getColorFor} = useColorManager();
    const [show, setShow] = useState(false);
    const [isCollectionMenuOpen, setCollectionMenuOpen] = useState(false);
    const [activeCategory, setActiveCategory] = useState<string>(allCategory);
    const [collectionValueToScrollTo, setCollectionValueToScrollTo] = useState<
        string | null
    >(null);
    const selectedCollectionRefs = useRef<
        Record<string, HTMLDivElement | null>
    >({});
    const currentCollectionIds = useMemo(
        () => collections.map(collection => collection.id),
        [collections]
    );
    const collectionSourceKey = [...currentCollectionIds]
        .sort((a, b) => a - b)
        .join(',');
    const committedCollectionIds = savedSelection.map(item => Number(item.value));
    const addedCollections = selected.filter(
        item => !savedSelection.some(saved => saved.value === item.value)
    );
    const removedCollections = savedSelection.filter(
        item => !selected.some(draft => draft.value === item.value)
    );
    const hasCollectionChanges =
        addedCollections.length > 0 || removedCollections.length > 0;

    // Expose open() to parent via ref
    if (openRef) {
        openRef.current = {open: () => setShow(true)};
    }

    const dispatch = useDispatch();
    const {data: respectCollaborationData} =
        useFetchCollectionPreviewRespectCollaborationQuery(undefined, {
            skip: !isEditable,
        });
    const {data: ownedAndFriendsData} =
        useFetchCollectionPreviewOwnedAndFriendsQuery(undefined, {
            skip: isEditable,
        });
    const {data: lastUsedCollectionIdsData} =
        useFetchLastProblemCollectionIdsQuery();
    const [saveProblemCollections] = useSaveProblemCollectionsMutation();
    const [saveLastProblemCollectionId] =
        useSaveLastProblemCollectionIdMutation();
    const [addToFavorites] = useAddToFavoritesMutation();
    const [removeFromFavorites] = useRemoveFromFavoritesMutation();
    const [createCollectionSuggestions] =
        useCreateCollectionSuggestionsMutation();

    const {user: authUser} = useSelector(state => state.auth);
    const {data: respectPrivacyData} = useGetCollectionRespectPrivacyQuery(
        undefined,
        {skip: !authUser}
    );

    const {data: existingSuggestionsData, refetch: refetchExistingSuggestions} =
        useFetchCollectionSuggestionsQuery(
            {
                criteria: {
                    problemId,
                    status: SuggestedProblemEditStatusEnum.OPEN,
                    limit: 100,
                    offset: 0,
                    sortDirection: 'desc',
                    sortBy: 'id',
                },
            },
            {skip: !authUser || !show}
        );
    const existingSuggestions = existingSuggestionsData?.entries ?? [];
    const existingSuggestionCollectionIds = new Set(
        existingSuggestions.map(s => s.collection.id)
    );

    const [pendingSuggestionCollectionIds, setPendingSuggestionCollectionIds] =
        useState<Set<number>>(new Set());
    const [suggestionComment, setSuggestionComment] = useState('');
    const [isSubmittingSuggestion, setIsSubmittingSuggestion] = useState(false);
    const menuScrollRef = useRef<HTMLDivElement | null>(null);
    const menuScrollTop = useRef<number>(0);

    useEffect(() => {
        const synchronizeSelection = (
            items: OptionType[],
            available: OptionType[] = items
        ) => {
            const source = appliedCollectionSource.current;
            if (
                source?.problemId !== problemId ||
                (!draftDirty.current &&
                    !collectionSaveInFlight.current &&
                    source.key !== collectionSourceKey)
            ) {
                appliedCollectionSource.current = {
                    problemId,
                    key: collectionSourceKey,
                };
                draftDirty.current = false;
                setSelected(items);
                setSavedSelection(items);
                setCollectionSaveError(null);
                setShowDiscardConfirmation(false);
                return;
            }

            // Refresh labels/permissions without replacing draft membership or a
            // successful local save while the problem query still has old data.
            const byValue = new Map(
                [...available, ...items].map(item => [item.value, item])
            );
            const refresh = (previous: OptionType[]) =>
                previous.map(item => byValue.get(item.value) ?? item);
            setSelected(refresh);
            setSavedSelection(refresh);
        };
        const data = isEditable
            ? respectCollaborationData
            : ownedAndFriendsData;
        if (!data?.entries) {
            synchronizeSelection(
                uniqueOptionsByValue(
                    collections.map(collection => ({
                        label: collection.name,
                        value: `${collection.id}`,
                        color: getCollectionTypeColor(collection.group),
                        core: collection.core,
                        isSuggestion: true,
                    }))
                )
            );
            return;
        }

        const transformedOptions = transformData(data.entries, getColorFor);
        const collectionsById = new Map(
            data.entries.map(entry => [entry.id, entry])
        );
        const lastUsedCollectionOptions = [
            ...(lastUsedCollectionIdsData?.lastProblemCollectionIds ?? []),
        ]
            .reverse()
            .flatMap(id => {
                const entry = collectionsById.get(id);
                return entry ? [entry] : [];
            })
            .map(entry => ({
                label: `${entry.name}`,
                value: `${entry.id}`,
                color: getColorFor(entry),
                core: entry.core,
            }));

        const nextOptions: OptionTypeWithLabel[] = [];
        if (lastUsedCollectionOptions.length > 0) {
            nextOptions.push({
                label: lastUsedCategory,
                options: lastUsedCollectionOptions,
            });
        }
        nextOptions.push(...transformedOptions);

        if (respectPrivacyData?.entries) {
            const mainIds = new Set(data.entries.map(e => e.id));
            const others = respectPrivacyData.entries.filter(
                c => !mainIds.has(c.id)
            );
            const othersByGroup: Record<string, OptionType[]> = {};
            others.forEach(c => {
                const group = c.group || t('common.other');
                if (!othersByGroup[group]) {
                    othersByGroup[group] = [];
                }
                othersByGroup[group].push({
                    label: c.name,
                    value: `${c.id}`,
                    color: getColorFor(c),
                    isSuggestion: true,
                    core: c.core,
                });
            });
            Object.entries(othersByGroup).forEach(
                ([groupName, suggestionItems]) => {
                    const existing = nextOptions.find(
                        g => g.label === groupName
                    );
                    if (existing) {
                        existing.options = [
                            ...existing.options,
                            ...suggestionItems,
                        ];
                    } else {
                        nextOptions.push({
                            label: groupName,
                            options: suggestionItems,
                        });
                    }
                }
            );
        }

        setOptions(nextOptions);

        const editableCollectionIds = new Set(
            data.entries.map(entry => entry.id)
        );
        const optionByCollectionId = new Map<number, OptionType>();
        nextOptions.forEach(group => {
            group.options.forEach(option => {
                optionByCollectionId.set(Number(option.value), option);
            });
        });

        const selectedCollectionSource =
            collections.length > 0
                ? collections
                : data.entries.filter(entry =>
                      currentCollectionIds.includes(entry.id)
                  );

        const selectedItems = uniqueOptionsByValue(
            selectedCollectionSource.map(collection => {
                const matchingOption = optionByCollectionId.get(collection.id);

                return {
                    label: collection.name,
                    value: `${collection.id}`,
                    color:
                        matchingOption?.color ??
                        getCollectionTypeColor(collection.group),
                    core: matchingOption?.core ?? collection.core,
                    isSuggestion:
                        matchingOption?.isSuggestion ??
                        !editableCollectionIds.has(collection.id),
                };
            })
        );

        synchronizeSelection(
            selectedItems,
            nextOptions.flatMap(group => group.options)
        );
    }, [
        collections,
        collectionSourceKey,
        currentCollectionIds,
        getColorFor,
        isEditable,
        lastUsedCategory,
        lastUsedCollectionIdsData,
        ownedAndFriendsData,
        respectCollaborationData,
        respectPrivacyData,
        problemId,
        hasCollectionChanges,
        isSavingCollections,
        t,
    ]);

    useEffect(() => {
        if (!show || !hasCollectionChanges) return;
        const warnBeforeLeaving = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnBeforeLeaving);
        return () =>
            window.removeEventListener('beforeunload', warnBeforeLeaving);
    }, [show, hasCollectionChanges]);

    useEffect(() => {
        setFavourite(isFavourite);
    }, [isFavourite]);

    useEffect(() => {
        if (!collectionValueToScrollTo) return;

        selectedCollectionRefs.current[
            collectionValueToScrollTo
        ]?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
        });
        setCollectionValueToScrollTo(null);
    }, [collectionValueToScrollTo, selected]);

    const handleToggleOther = (id: number) => {
        setPendingSuggestionCollectionIds(prev => {
            if (prev.has(id)) return new Set();
            return new Set([id]);
        });
    };

    const handleSubmitSuggestions = async () => {
        if (pendingSuggestionCollectionIds.size === 0) return;
        setIsSubmittingSuggestion(true);
        try {
            const suggestions = Array.from(pendingSuggestionCollectionIds).map(
                collectionId => ({
                    collectionId,
                    type: committedCollectionIds.includes(collectionId)
                        ? CollectionSuggestionTypeEnum.REMOVE
                        : CollectionSuggestionTypeEnum.ADD,
                })
            );
            await createCollectionSuggestions({
                problemId,
                suggestions,
                comment: suggestionComment || undefined,
            }).unwrap();
            toast.success(t('problem_collections.add_to.suggestions_submitted'), {
                autoClose: 1500,
            });
            setPendingSuggestionCollectionIds(new Set());
            setSuggestionComment('');
            void refetchExistingSuggestions();
        } catch (error) {
            const message =
                (error as {data?: {message?: string}})?.data?.message ??
                t('problem_collections.add_to.suggestions_submit_error');
            toast.error(message);
        } finally {
            setIsSubmittingSuggestion(false);
        }
    };

    const updateSelectedCollections = (items: OptionType[]) => {
        if (collectionSaveInFlight.current) return;
        const nextSelected = uniqueOptionsByValue(items);
        draftDirty.current =
            nextSelected.length !== savedSelection.length ||
            nextSelected.some(
                item => !savedSelection.some(saved => saved.value === item.value)
            );
        const added = nextSelected.find(
            item => !selected.some(previous => previous.value === item.value)
        );
        setSelected(nextSelected);
        setCollectionSaveError(null);
        if (added) setCollectionValueToScrollTo(added.value);
    };

    const resetCollectionDraft = () => {
        draftDirty.current = false;
        setSelected(savedSelection);
        setCollectionSaveError(null);
    };

    const handleSaveCollections = async () => {
        if (!hasCollectionChanges || collectionSaveInFlight.current) return;
        collectionSaveInFlight.current = true;
        setIsSavingCollections(true);
        setCollectionSaveError(null);
        const nextSelected = [...selected];
        const addedCollectionIds = addedCollections.map(item =>
            Number(item.value)
        );
        try {
            await saveProblemCollections({
                problemId,
                collections: nextSelected.map(item => Number(item.value)),
            }).unwrap();

            // Preserve use order across saves without delaying the primary save.
            if (addedCollectionIds.length > 0) {
                lastUsedCollectionUpdates.current =
                    lastUsedCollectionUpdates.current
                        .then(async () => {
                            for (const collectionId of addedCollectionIds) {
                                await saveLastProblemCollectionId(
                                    collectionId
                                ).unwrap();
                            }
                        })
                        .catch(error => {
                            console.warn(
                                'Unable to update recently used collections',
                                error
                            );
                        });
            }

            if (appliedCollectionSource.current?.problemId !== problemId) return;
            appliedCollectionSource.current = {
                problemId,
                key: collectionSourceKey,
            };
            draftDirty.current = false;
            setSavedSelection(nextSelected);
            dispatch(
                problemsApi.util.invalidateTags([{type: 'problem', id: problemId}])
            );
            toast.success(t('problem_collections.add_to.collections_saved'), {
                autoClose: 1500,
            });
        } catch (error) {
            if (appliedCollectionSource.current?.problemId !== problemId) return;
            setCollectionSaveError(
                (error as {data?: {message?: string}})?.data?.message ??
                    t('problem_collections.add_to.collections_save_error')
            );
        } finally {
            collectionSaveInFlight.current = false;
            setIsSavingCollections(false);
        }
    };

    const closeModal = () => {
        resetCollectionDraft();
        setCollectionMenuOpen(false);
        setShowDiscardConfirmation(false);
        setShow(false);
    };

    const handleClose = () => {
        if (collectionSaveInFlight.current || showDiscardConfirmation) return;
        setCollectionMenuOpen(false);
        if (hasCollectionChanges) {
            setShowDiscardConfirmation(true);
        } else {
            closeModal();
        }
    };

    const handleDismiss = () => {
        if (isCollectionMenuOpen) {
            setCollectionMenuOpen(false);
            return;
        }
        handleClose();
    };

    const CustomMenuList = (
        props: MenuListProps<OptionType, true, OptionTypeWithLabel>
    ) => {
        const theme = useTheme();
        const isDark = theme === 'dark';

        const groups = Array.from(props.options).filter(
            (o): o is OptionTypeWithLabel => 'options' in o
        );

        const searchInput = (props.selectProps.inputValue ?? '').trim();
        const isSearching = searchInput.length > 0;
        const searchLower = searchInput.toLowerCase();

        const searchedGroups = isSearching
            ? groups
                  .map(g => ({
                      ...g,
                      options: g.options.filter(o =>
                          o.label.toLowerCase().includes(searchLower)
                      ),
                  }))
                  .filter(g => g.options.length > 0)
            : groups;

        const filteredOptions = isSearching
            ? searchedGroups
            : activeCategory === allCategory
              ? groups
              : activeCategory === myCollectionsCategory
                ? groups
                      .filter(g => g.label !== lastUsedCategory)
                      .map(g => ({
                          ...g,
                          options: g.options.filter(o => !o.isSuggestion),
                      }))
                      .filter(g => g.options.length > 0)
                : groups.filter(g => g.label === activeCategory);

        const sidebarCategories = isSearching
            ? [searchCategory]
            : [
                  allCategory,
                  ...groups
                      .filter(g => g.label === lastUsedCategory)
                      .map(g => g.label),
                  ...(!isEditable ? [myCollectionsCategory] : []),
                  ...groups
                      .filter(g => g.label !== lastUsedCategory)
                      .map(g => g.label),
              ];

        const sidebarBg = isDark ? '#1e1e1e' : '#f5f5f5';
        const borderColor = isDark ? '#3a3a3a' : '#e0e0e0';
        const activeItemBg = isDark ? '#333333' : '#e8e8e8';
        const activeItemColor = isDark ? '#ffffff' : '#1a1a1a';
        const itemColor = isDark ? '#a0a0a0' : '#666666';
        const hoverBg = isDark ? '#2a2a2a' : '#ebebeb';
        const headerColor = isDark ? '#888888' : '#888888';
        const textColor = isDark ? '#e0e0e0' : '#333333';
        const rowHoverBg = isDark ? '#2f2f2f' : '#f0f0f0';

        return (
            <div
                onMouseDown={e => e.preventDefault()}
                style={{
                    display: 'flex',
                    height: Math.min(props.maxHeight, 500),
                    maxHeight: Math.min(props.maxHeight, 500),
                }}>
                <div
                    style={{
                        width: '140px',
                        flexShrink: 0,
                        borderRight: `1px solid ${borderColor}`,
                        backgroundColor: sidebarBg,
                        overflowY: 'auto',
                        padding: '4px 0',
                    }}>
                    {sidebarCategories.map(cat => {
                        const isActive = isSearching
                            ? cat === searchCategory
                            : activeCategory === cat;
                        return (
                            <button
                                key={cat}
                                onClick={() => {
                                    if (!isSearching) setActiveCategory(cat);
                                }}
                                onMouseEnter={e => {
                                    if (!isActive) {
                                        (
                                            e.target as HTMLElement
                                        ).style.backgroundColor = hoverBg;
                                    }
                                }}
                                onMouseLeave={e => {
                                    if (!isActive) {
                                        (
                                            e.target as HTMLElement
                                        ).style.backgroundColor = 'transparent';
                                    }
                                }}
                                style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '7px 12px',
                                    border: 'none',
                                    borderLeft: isActive
                                        ? `3px solid ${activeItemColor}`
                                        : '3px solid transparent',
                                    backgroundColor: isActive
                                        ? activeItemBg
                                        : 'transparent',
                                    color: isActive
                                        ? activeItemColor
                                        : itemColor,
                                    cursor: isSearching ? 'default' : 'pointer',
                                    fontSize: '0.82rem',
                                    textAlign: 'left',
                                    fontWeight: isActive ? 600 : 400,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}>
                                {cat}
                            </button>
                        );
                    })}
                </div>

                <div
                    ref={el => {
                        menuScrollRef.current = el;
                        if (el) {
                            el.scrollTop = menuScrollTop.current;
                        }
                    }}
                    onScroll={e => {
                        menuScrollTop.current = (
                            e.target as HTMLDivElement
                        ).scrollTop;
                    }}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '4px 8px',
                    }}>
                    {filteredOptions.map((group, gi) => (
                        <div key={group.label}>
                            {(activeCategory === allCategory ||
                                activeCategory === myCollectionsCategory ||
                                gi === 0) && (
                                <div
                                    style={{
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        color: headerColor,
                                        padding: '6px 4px 4px',
                                        ...(gi > 0
                                            ? {
                                                  borderTop: `1px solid ${borderColor}`,
                                                  marginTop: '4px',
                                                  paddingTop: '8px',
                                              }
                                            : {}),
                                    }}>
                                    {group.label}
                                </div>
                            )}
                            {group.options?.map((collection: OptionType) => {
                                const isSuggestion =
                                    collection.isSuggestion === true;
                                const suggestionItems = group.options.filter(
                                    o => o.isSuggestion
                                );
                                const isFirstSuggestion =
                                    isSuggestion &&
                                    suggestionItems[0]?.value ===
                                        collection.value;
                                const showDivider = isFirstSuggestion;

                                const colId = Number(collection.value);
                                const isInCollection =
                                    committedCollectionIds.includes(colId);
                                const hasPendingSuggestion =
                                    pendingSuggestionCollectionIds.has(colId);
                                const isChecked = isSuggestion
                                    ? isInCollection !== hasPendingSuggestion
                                    : selected.some(
                                          s => s.value === collection.value
                                      );

                                const handleClick = () => {
                                    if (collectionSaveInFlight.current) return;
                                    if (isSuggestion) {
                                        handleToggleOther(
                                            Number(collection.value)
                                        );
                                        return;
                                    }
                                    const nextSelected = isChecked
                                        ? selected.filter(
                                              s => s.value !== collection.value
                                          )
                                        : [...selected, collection];

                                    updateSelectedCollections(
                                        nextSelected
                                    );
                                };

                                const suggestionAction = t(
                                    isInCollection
                                        ? 'problem_collections.add_to.suggest_remove'
                                        : 'problem_collections.add_to.suggest_add'
                                );

                                return (
                                    <Fragment key={collection.value}>
                                        {showDivider && (
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    margin: '6px 6px 4px',
                                                }}>
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        borderTop: `1px solid ${borderColor}`,
                                                    }}
                                                />
                                                <span
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600,
                                                        textTransform:
                                                            'uppercase',
                                                        letterSpacing: '0.05em',
                                                        color: headerColor,
                                                        flexShrink: 0,
                                                    }}>
                                                    {t('problem_collections.add_to.suggest')}
                                                </span>
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        borderTop: `1px solid ${borderColor}`,
                                                    }}
                                                />
                                            </div>
                                        )}
                                        <div
                                            key={collection.value}
                                            onMouseDown={e =>
                                                e.preventDefault()
                                            }
                                            onClick={handleClick}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.backgroundColor =
                                                    rowHoverBg;
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.backgroundColor =
                                                    'transparent';
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '5px 6px',
                                                cursor: 'pointer',
                                                borderRadius: '4px',
                                                fontSize: '0.875rem',
                                                color: textColor,
                                            }}>
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                disabled={isSavingCollections}
                                                readOnly
                                                style={{
                                                    accentColor: isSuggestion
                                                        ? '#047857'
                                                        : collection.color,
                                                    width: '16px',
                                                    height: '16px',
                                                    flexShrink: 0,
                                                    cursor: 'pointer',
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            {collection.core && (
                                                <DiamondFill
                                                    size={9}
                                                    style={{
                                                        color: '#fbbf24',
                                                        flexShrink: 0,
                                                    }}
                                                    title={t('problem_collections.core_collection')}
                                                />
                                            )}
                                            {collection.color && (
                                                <span
                                                    style={{
                                                        display: 'inline-block',
                                                        width: '10px',
                                                        height: '10px',
                                                        backgroundColor:
                                                            collection.color,
                                                        borderRadius: '2px',
                                                        flexShrink: 0,
                                                    }}
                                                />
                                            )}
                                            <span style={{flex: 1}}>
                                                {collection.label}
                                            </span>
                                            {existingSuggestionCollectionIds.has(
                                                colId
                                            ) &&
                                                !hasPendingSuggestion && (
                                                    <span
                                                        style={{
                                                            fontSize: '0.65rem',
                                                            padding: '1px 5px',
                                                            borderRadius: '3px',
                                                            backgroundColor:
                                                                '#737373',
                                                            color: '#fff',
                                                            flexShrink: 0,
                                                        }}>
                                                        {t('problem_collections.add_to.pending')}
                                                    </span>
                                                )}
                                            {isSuggestion &&
                                                hasPendingSuggestion && (
                                                    <span
                                                        style={{
                                                            fontSize: '0.7rem',
                                                            padding: '1px 6px',
                                                            borderRadius: '3px',
                                                            backgroundColor:
                                                                isInCollection
                                                                    ? '#dc2626'
                                                                    : '#047857',
                                                            color: '#fff',
                                                            flexShrink: 0,
                                                        }}>
                                                        {suggestionAction}
                                                    </span>
                                                )}
                                        </div>
                                    </Fragment>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const favoriteActionLabel = t(
        favourite
            ? 'problem_collection.remove_from_favorites'
            : 'problem_collection.add_to_favorites'
    );

    const handleToggleFavorite = async () => {
        if (favoriteUpdateInFlight.current) return;
        favoriteUpdateInFlight.current = true;
        setIsUpdatingFavorite(true);
        try {
            if (favourite) {
                await removeFromFavorites({userId, problemId}).unwrap();
                setFavourite(false);
            } else {
                await addToFavorites({userId, problemId}).unwrap();
                setFavourite(true);
            }
            dispatch(
                problemsApi.util.invalidateTags([{type: 'problem', id: problemId}])
            );
        } catch (error) {
            const message =
                (error as {data?: {message?: string}})?.data?.message ??
                'Failed to update favorites';
            toast.error(message);
        } finally {
            favoriteUpdateInFlight.current = false;
            setIsUpdatingFavorite(false);
        }
    };

    return (
        <div className="problem-collection-section contents">
            <Modal
                show={show}
                onHide={handleDismiss}
                size="lg"
                dialogClassName="problem-add-to-modal"
                aria-labelledby={modalTitleId}>
                <Modal.Header>
                    <Modal.Title id={modalTitleId}>
                        {t('problem_collections.add_to.title')}
                    </Modal.Title>
                    <button
                        type="button"
                        className="ml-4 rounded p-1 text-2xl leading-none opacity-60 hover:bg-neutral-100 hover:opacity-100 dark:hover:bg-[var(--ui-surface-muted)]"
                        aria-label={t('common.close')}
                        onClick={handleClose}>
                        ×
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <div className="problem-destination-actions">
                        <ProblemDestinationToggle
                            title={t('problem_collections.add_to.favorites')}
                            actionLabel={favoriteActionLabel}
                            icon={<FontAwesomeIcon icon={faHeart} />}
                            selected={favourite}
                            busy={isUpdatingFavorite}
                            onClick={handleToggleFavorite}
                        />
                        <TrainingProblemToggle problemId={problemId} />
                    </div>
                    <div className="problem-add-to-suggestions">
                        {existingSuggestions.length > 0 && (
                            <OverlayTrigger
                                trigger="click"
                                placement="bottom"
                                rootClose
                                overlay={
                                    <Popover
                                        id="existing-suggestions-popover"
                                        className="problem-add-to-open-suggestions">
                                        <Popover.Body className="p-2">
                                            <div className="d-flex flex-column gap-1">
                                                {existingSuggestions.map(s => (
                                                    <div
                                                        key={s.id}
                                                        className="problem-suggestion-row">
                                                        <span
                                                            className="problem-suggestion-type"
                                                            data-type={s.type}>
                                                            {s.type}
                                                        </span>
                                                        <Link
                                                            className="problem-suggestion-collection"
                                                            to={`/collections/${s.collection.id}`}
                                                            title={s.collection.name}
                                                            target="_blank"
                                                            rel="noopener noreferrer">
                                                            {s.collection.name}
                                                        </Link>
                                                        <span
                                                            className="problem-suggestion-author"
                                                            title={s.user?.name}>
                                                            {t('common.by')}{' '}
                                                            <UserItem
                                                                user={s.user}
                                                                showAvatar={
                                                                    false
                                                                }
                                                                showLevel={
                                                                    false
                                                                }
                                                                target="_blank"
                                                                className="min-w-0"
                                                                textClassName="!mx-0 !font-normal min-w-0"
                                                            />
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </Popover.Body>
                                    </Popover>
                                }>
                                <span
                                    className="ms-auto text-muted"
                                    role="button"
                                    style={{
                                        fontSize: '0.8rem',
                                        cursor: 'pointer',
                                    }}>
                                    {t('problem_collections.add_to.open_suggestions', {
                                        count: existingSuggestions.length,
                                    })}
                                </span>
                            </OverlayTrigger>
                        )}
                    </div>

                    {/* Pending suggestion area */}
                    {pendingSuggestionCollectionIds.size > 0 &&
                        (() => {
                            const pendingId = Array.from(
                                pendingSuggestionCollectionIds
                            )[0];
                            const isAdd =
                                !committedCollectionIds.includes(pendingId);
                            return (
                                <div className="problem-add-to-pending">
                                    <div className="d-flex align-items-center gap-2 mb-2">
                                        <h6
                                            className="mb-0"
                                            style={{fontSize: '0.85rem'}}>
                                            {isAdd
                                                ? t('problem_collections.add_to.suggest_adding')
                                                : t('problem_collections.add_to.suggest_removing')}
                                        </h6>
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                <Tooltip id="suggestion-info-tooltip">
                                                    {t('problem_collections.add_to.review_note')}
                                                </Tooltip>
                                            }>
                                            <Link
                                                to="/article/collection-suggestions"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-muted"
                                                style={{fontSize: '0.85rem'}}>
                                                <InfoCircle />
                                            </Link>
                                        </OverlayTrigger>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {Array.from(
                                            pendingSuggestionCollectionIds
                                        ).map(id => {
                                            const col =
                                                options
                                                    .flatMap(g => g.options)
                                                    .find(
                                                        o =>
                                                            Number(o.value) ===
                                                            id
                                                    ) ??
                                                selected.find(
                                                    o => Number(o.value) === id
                                                );
                                            return (
                                                <span
                                                    key={id}
                                                    className="problem-add-to-chip problem-add-to-pending-chip">
                                                    {col?.core && (
                                                        <DiamondFill
                                                            size={10}
                                                            className="text-warning"
                                                        />
                                                    )}
                                                    <span
                                                        className="problem-add-to-chip-color"
                                                        aria-hidden="true"
                                                        style={{
                                                            backgroundColor:
                                                                col?.color ??
                                                                DEFAULT_COLLECTION_COLOR,
                                                        }}
                                                    />
                                                    <span className="problem-add-to-chip-label">
                                                        {col?.label ?? `#${id}`}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        className="problem-add-to-chip-remove"
                                                        aria-label={`${t('common.cancel')}: ${col?.label ?? `#${id}`}`}
                                                        onClick={() =>
                                                            handleToggleOther(
                                                                id
                                                            )
                                                        }>
                                                        <FontAwesomeIcon
                                                            icon={faXmark}
                                                            aria-hidden="true"
                                                        />
                                                    </button>
                                                </span>
                                            );
                                        })}
                                    </div>
                                    <div className="problem-add-to-pending-form">
                                        <Form.Control
                                            type="text"
                                            placeholder={t('common.comment_optional')}
                                            value={suggestionComment}
                                            onChange={e =>
                                                setSuggestionComment(
                                                    e.target.value
                                                )
                                            }
                                            size="sm"
                                            className="flex-grow-1"
                                        />
                                        <Button
                                            size="sm"
                                            variant="primary"
                                            disabled={isSubmittingSuggestion}
                                            onClick={handleSubmitSuggestions}
                                            style={{whiteSpace: 'nowrap'}}>
                                            {isSubmittingSuggestion
                                                ? t('common.submitting')
                                                : t('common.submit')}
                                        </Button>
                                    </div>
                                </div>
                            );
                        })()}

                    <section aria-labelledby={`${modalTitleId}-collections`}>
                        <div className="problem-add-to-collections-heading">
                            <h6 id={`${modalTitleId}-collections`}>
                                {t('problem_collections.add_to.collections')}
                                <span className="problem-add-to-count">
                                    {selected.length}
                                </span>
                            </h6>
                            <Link
                                className="small text-decoration-none"
                                to="/collections/create">
                                {t(
                                    'problem_collection_create.create_collection_link'
                                )}
                            </Link>
                        </div>
                        <div
                            className="problem-add-to-selected"
                            role={selected.length > 0 ? 'list' : undefined}
                            aria-label={t(
                                'problem_collections.add_to.collections'
                            )}>
                            {selected.length === 0 ? (
                                <div className="problem-add-to-empty">
                                    {t(
                                        'problem_collections.problem_not_in_any_collections'
                                    )}
                                </div>
                            ) : (
                                selected.map(collection => (
                                    <div
                                        key={collection.value}
                                        role="listitem"
                                        ref={element => {
                                            selectedCollectionRefs.current[
                                                collection.value
                                            ] = element;
                                        }}
                                        className="problem-add-to-chip">
                                        {collection.core && (
                                            <DiamondFill
                                                size={10}
                                                style={{color: '#fbbf24'}}
                                                title={t('problem_collections.core_collection')}
                                            />
                                        )}
                                        {collection.color && (
                                            <span
                                                style={{
                                                    display: 'inline-block',
                                                    width: '10px',
                                                    height: '10px',
                                                    backgroundColor:
                                                        collection.color,
                                                    borderRadius: '2px',
                                                    flexShrink: 0,
                                                }}
                                            />
                                        )}
                                        <span
                                            className="problem-add-to-chip-label">
                                            {collection.label}
                                        </span>
                                        <button
                                            type="button"
                                            className="problem-add-to-chip-remove"
                                            disabled={isSavingCollections}
                                            aria-label={t(
                                                collection.isSuggestion
                                                    ? 'problem_collections.add_to.suggest_remove_collection'
                                                    : 'problem_collections.add_to.remove_collection',
                                                {name: collection.label}
                                            )}
                                            title={t(
                                                collection.isSuggestion
                                                    ? 'problem_collections.add_to.suggest_remove_collection'
                                                    : 'problem_collections.add_to.remove_collection',
                                                {name: collection.label}
                                            )}
                                            onClick={() => {
                                                if (collection.isSuggestion) {
                                                    handleToggleOther(
                                                        Number(collection.value)
                                                    );
                                                    return;
                                                }

                                                updateSelectedCollections(
                                                    selected.filter(
                                                        s =>
                                                            s.value !==
                                                            collection.value
                                                    )
                                                );
                                            }}>
                                            <FontAwesomeIcon icon={faXmark} />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                        <CollectionLegend options={options} />
                        <ThemeAwareSelect
                            className="w-full"
                            placeholder={t(
                                'problem_collections.add_to.search'
                            )}
                            aria-label={t(
                                'problem_collections.add_to.search'
                            )}
                            isMulti
                            isDisabled={isSavingCollections}
                            closeMenuOnSelect={false}
                            menuIsOpen={isCollectionMenuOpen}
                            onMenuOpen={() => setCollectionMenuOpen(true)}
                            onMenuClose={() => setCollectionMenuOpen(false)}
                            onKeyDown={event => {
                                if (
                                    event.key === 'Escape' &&
                                    isCollectionMenuOpen
                                ) {
                                    event.stopPropagation();
                                }
                            }}
                            options={options}
                            value={selected}
                            isClearable={false}
                            controlShouldRenderValue={false}
                            styles={colorStyles}
                            components={{MenuList: CustomMenuList}}
                            formatOptionLabel={(data: OptionType) => (
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}>
                                    {data.core && (
                                        <DiamondFill
                                            size={8}
                                            style={{color: '#fbbf24'}}
                                            title={t('problem_collections.core_collection')}
                                        />
                                    )}
                                    {data.label}
                                </span>
                            )}
                            onChange={changedSelected => {
                                const nextSelected = changedSelected
                                    ? Array.from(changedSelected)
                                    : [];
                                updateSelectedCollections(
                                    nextSelected as OptionType[]
                                );
                            }}
                        />
                        {hasCollectionChanges && (
                            <div
                                className="problem-add-to-changes"
                                aria-busy={isSavingCollections}>
                                <div className="problem-add-to-changes-title">
                                    {t(
                                        'problem_collections.add_to.unsaved_changes'
                                    )}
                                </div>
                                <ul className="problem-add-to-changes-list">
                                    {(
                                        [
                                            {type: 'add', items: addedCollections},
                                            {
                                                type: 'remove',
                                                items: removedCollections,
                                            },
                                        ] as const
                                    ).flatMap(({type, items}) =>
                                        items.map(item => (
                                            <li
                                                key={`${type}:${item.value}`}
                                                className="problem-add-to-change"
                                                data-type={type}>
                                                <span
                                                    className="problem-add-to-change-mark"
                                                    aria-hidden="true">
                                                    {type === 'add' ? '+' : '−'}
                                                </span>
                                                <span className="sr-only">
                                                    {t(`common.${type}`)}:{' '}
                                                </span>
                                                <span className="problem-add-to-chip-label">
                                                    {item.label}
                                                </span>
                                            </li>
                                        ))
                                    )}
                                </ul>
                                {collectionSaveError && (
                                    <div
                                        className="problem-add-to-save-error"
                                        role="alert">
                                        {collectionSaveError}
                                    </div>
                                )}
                                <div className="problem-add-to-save-actions">
                                    <Button
                                        size="sm"
                                        variant="light"
                                        disabled={isSavingCollections}
                                        onClick={resetCollectionDraft}>
                                        {t(
                                            'problem_collections.add_to.revert_changes'
                                        )}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="primary"
                                        disabled={isSavingCollections}
                                        onClick={() => void handleSaveCollections()}>
                                        {t(
                                            isSavingCollections
                                                ? 'problem_collections.add_to.saving'
                                                : 'problem_collections.add_to.save_changes'
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </section>
                </Modal.Body>
            </Modal>
            <ConfirmModal
                show={showDiscardConfirmation}
                title={t('problem_collections.add_to.discard_title')}
                description={t('problem_collections.add_to.discard_description')}
                okText={t('problem_collections.add_to.discard_changes')}
                okVariant="danger"
                cancelText={t('problem_collections.add_to.keep_editing')}
                onOk={closeModal}
                onHide={() => setShowDiscardConfirmation(false)}
            />
            <button
                type="button"
                className="problem-favorite-action"
                aria-label={favoriteActionLabel}
                title={favoriteActionLabel}
                aria-pressed={favourite}
                aria-busy={isUpdatingFavorite}
                disabled={isUpdatingFavorite}
                onClick={handleToggleFavorite}>
                {isUpdatingFavorite ? (
                    <FontAwesomeIcon
                        icon={faSpinner}
                        spin
                        fixedWidth
                        aria-hidden="true"
                    />
                ) : (
                    <FontAwesomeIcon
                        icon={favourite ? faHeart : faHeartOutline}
                        aria-hidden="true"
                        focusable="false"
                    />
                )}
            </button>
            {beforeAddTo}
            <Button
                variant="secondary"
                size="sm"
                className="max-h-fit"
                onClick={() => setShow(true)}>
                {t('problem_collections.add_to.short_title')}
            </Button>
        </div>
    );
};

export default ProblemCollectionDropdown;
